
/**
 * Central API service for making requests to the backend
 * This service handles domain differences between development and production environments
 */

import axios from 'axios';
import { getServerUrl, deploymentEnv } from '../config';

// Create a base axios instance with the correct base URL
const apiClient = axios.create({
  baseURL: getBaseApiUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Get the base API URL based on the current environment
 * @returns {string} The base API URL
 */
function getBaseApiUrl() {
  if (deploymentEnv === 'development') {
    // In development, use the relative URL which will be handled by the proxy
    return '';
  } else {
    // Use centralized config for beta and production
    return getServerUrl('proxy');
  }
}

/**
 * Make a GET request to the API
 * @param {string} endpoint - The API endpoint
 * @param {object} params - Query parameters
 * @returns {Promise} - The axios promise
 */
export const get = (endpoint, params = {}) => {
  return apiClient.get(endpoint, { params });
};

/**
 * Make a POST request to the API
 * @param {string} endpoint - The API endpoint
 * @param {object} data - The request body
 * @param {object} config - Additional axios config
 * @returns {Promise} - The axios promise
 */
export const post = (endpoint, data = {}, config = {}) => {
  return apiClient.post(endpoint, data, config);
};

/**
 * Make a PUT request to the API
 * @param {string} endpoint - The API endpoint
 * @param {object} data - The request body
 * @returns {Promise} - The axios promise
 */
export const put = (endpoint, data = {}) => {
  return apiClient.put(endpoint, data);
};

/**
 * Make a DELETE request to the API
 * @param {string} endpoint - The API endpoint
 * @returns {Promise} - The axios promise
 */
export const del = (endpoint) => {
  return apiClient.delete(endpoint);
};

/**
 * Make a GraphQL request to the Shopify API through our proxy
 * @param {string} query - The GraphQL query
 * @param {object} variables - The GraphQL variables
 * @param {string} shop - The shop domain
 * @param {string} accessToken - The shop access token
 * @returns {Promise} - The axios promise
 */
export const graphql = (query, variables = null, shop, accessToken) => {
  const requestBody = {
    query,
    variables,
    shop,
    accessToken
  };
  
  return post('/api/shopify/graphql', requestBody);
};

export default {
  get,
  post,
  put,
  delete: del,
  graphql,
  getBaseApiUrl
};
